import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    IconButton,
    Tooltip,
    Alert,
    CircularProgress,
    Grid,
    Pagination,
    Stack,
    Divider,
    useMediaQuery,
    useTheme as useMuiTheme
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Visibility as ViewIcon,
    Search as SearchIcon,
    Clear as ClearIcon,
    CheckCircle as ActiveIcon,
    Cancel as InactiveIcon,
    Work as WorkIcon
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
    fetchJobPostings,
    deleteJobPosting,
    toggleJobPostingStatus,
    setFilters,
    clearFilters
} from '../../store/slices/jobPostingSlice';
import { JobPosting } from '../../services/jobPostingApi';
import { useTheme } from '../../contexts/ThemeContext';
import JobPostingForm from './JobPostingForm';
import JobPostingView from './JobPostingView';
import ConfirmationDialog from '../../components/ConfirmationDialog';

const JobPostings: React.FC = () => {
    const { theme } = useTheme();
    const muiTheme = useMuiTheme();
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
    const dispatch = useAppDispatch();
    
    const { jobPostings, pagination, filters, loading, error } = useAppSelector((state) => state.jobPostings);

    // Local state
    const [searchQuery, setSearchQuery] = useState(filters.search);
    const [selectedStatus, setSelectedStatus] = useState(filters.status);
    const [currentPage, setCurrentPage] = useState(1);
    const [openForm, setOpenForm] = useState(false);
    const [openView, setOpenView] = useState(false);
    const [selectedJobPosting, setSelectedJobPosting] = useState<JobPosting | null>(null);
    const [deleteDialog, setDeleteDialog] = useState(false);
    const [jobPostingToDelete, setJobPostingToDelete] = useState<JobPosting | null>(null);

    // Load job postings on component mount
    useEffect(() => {
        loadJobPostings();
    }, [currentPage, filters]);

    const loadJobPostings = () => {
        const params = {
            page: currentPage,
            per_page: 20,
            ...filters
        };
        dispatch(fetchJobPostings(params));
    };

    const handleSearch = () => {
        dispatch(setFilters({ search: searchQuery, page: 1 }));
        setCurrentPage(1);
    };

    const handleStatusFilter = (status: 'active' | 'inactive' | '') => {
        setSelectedStatus(status);
        dispatch(setFilters({ status, page: 1 }));
        setCurrentPage(1);
    };

    const handleClearFilters = () => {
        setSearchQuery('');
        setSelectedStatus('');
        dispatch(clearFilters());
        setCurrentPage(1);
    };

    const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
        setCurrentPage(page);
        dispatch(setFilters({ page }));
    };

    const handleAddJobPosting = () => {
        setSelectedJobPosting(null);
        setOpenForm(true);
    };

    const handleEditJobPosting = (jobPosting: JobPosting) => {
        setSelectedJobPosting(jobPosting);
        setOpenForm(true);
    };

    const handleViewJobPosting = (jobPosting: JobPosting) => {
        setSelectedJobPosting(jobPosting);
        setOpenView(true);
    };

    const handleDeleteJobPosting = (jobPosting: JobPosting) => {
        setJobPostingToDelete(jobPosting);
        setDeleteDialog(true);
    };

    const confirmDelete = async () => {
        if (jobPostingToDelete) {
            try {
                await dispatch(deleteJobPosting(jobPostingToDelete.id)).unwrap();
                setDeleteDialog(false);
                setJobPostingToDelete(null);
                loadJobPostings();
            } catch (error) {
                console.error('Error deleting job posting:', error);
            }
        }
    };

    const handleToggleStatus = async (jobPosting: JobPosting) => {
        try {
            await dispatch(toggleJobPostingStatus(jobPosting.id)).unwrap();
            loadJobPostings();
        } catch (error) {
            console.error('Error toggling job posting status:', error);
        }
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const truncateText = (text: string, maxLength: number = 100) => {
        if (text.length <= maxLength) return text;
        return text.substring(0, maxLength) + '...';
    };

    return (
        <Box sx={{ p: isMobile ? 2 : 3 }}>
            {/* Header */}
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <WorkIcon sx={{ fontSize: 32, color: theme.palette.primary.main }} />
                    <Typography variant="h4" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                        Job Postings
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAddJobPosting}
                    sx={{
                        bgcolor: theme.palette.secondary.main,
                        borderRadius: 3,
                        '&:hover': {
                            bgcolor: theme.palette.secondary.dark,
                        }
                    }}
                >
                    Add Job Posting
                </Button>
            </Box>

            {/* Error Alert */}
            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {/* Search and Filters */}
            <Card sx={{ mb: 3, borderRadius: 3 }}>
                <CardContent>
                    <Grid container spacing={2} alignItems="center">
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <TextField
                                fullWidth
                                placeholder="Search job postings..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                InputProps={{
                                    startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                                    sx: { borderRadius: 3 }
                                }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 3 }}>
                            <FormControl fullWidth>
                                <InputLabel>Status</InputLabel>
                                <Select
                                    value={selectedStatus}
                                    onChange={(e) => handleStatusFilter(e.target.value as 'active' | 'inactive' | '')}
                                    label="Status"
                                    sx={{ borderRadius: 3 }}
                                >
                                    <MenuItem value="">All Status</MenuItem>
                                    <MenuItem value="active">Active</MenuItem>
                                    <MenuItem value="inactive">Inactive</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 5 }}>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                <Button
                                    variant="outlined"
                                    onClick={handleClearFilters}
                                    startIcon={<ClearIcon />}
                                    sx={{ borderRadius: 3 }}
                                >
                                    Clear
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Job Postings Table */}
            {!isMobile ? (
                <Card sx={{ borderRadius: 3 }}>
                    <CardContent>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 600 }}>Title</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Location</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Applications</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {loading ? (
                                        <TableRow>
                                            <TableCell colSpan={7} align="center">
                                                <CircularProgress />
                                            </TableCell>
                                        </TableRow>
                                    ) : jobPostings.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={7} align="center">
                                                <Typography variant="body2" color="textSecondary">
                                                    No job postings found
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        jobPostings.map((jobPosting) => (
                                            <TableRow key={jobPosting.id} hover>
                                                <TableCell>
                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                        {jobPosting.title}
                                                    </Typography>
                                                    <Typography variant="caption" color="textSecondary">
                                                        {truncateText(jobPosting.description, 80)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {jobPosting.location || '-'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {jobPosting.job_type || '-'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={jobPosting.is_active ? 'Active' : 'Inactive'}
                                                        color={jobPosting.is_active ? 'success' : 'default'}
                                                        size="small"
                                                        icon={jobPosting.is_active ? <ActiveIcon /> : <InactiveIcon />}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {jobPosting.careers_count || 0}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {formatDate(jobPosting.created_at)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                                        <Tooltip title="View">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleViewJobPosting(jobPosting)}
                                                                sx={{ color: theme.palette.primary.main }}
                                                            >
                                                                <ViewIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Edit">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleEditJobPosting(jobPosting)}
                                                                sx={{ color: theme.palette.warning.main }}
                                                            >
                                                                <EditIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title={jobPosting.is_active ? 'Deactivate' : 'Activate'}>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleToggleStatus(jobPosting)}
                                                                sx={{ color: jobPosting.is_active ? theme.palette.error.main : theme.palette.success.main }}
                                                            >
                                                                {jobPosting.is_active ? <InactiveIcon /> : <ActiveIcon />}
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Delete">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleDeleteJobPosting(jobPosting)}
                                                                sx={{ color: theme.palette.error.main }}
                                                            >
                                                                <DeleteIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </CardContent>
                </Card>
            ) : (
                // Mobile View
                <Stack spacing={2}>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : jobPostings.length === 0 ? (
                        <Card sx={{ borderRadius: 3 }}>
                            <CardContent>
                                <Typography variant="body2" color="textSecondary" align="center">
                                    No job postings found
                                </Typography>
                            </CardContent>
                        </Card>
                    ) : (
                        jobPostings.map((jobPosting) => (
                            <Card key={jobPosting.id} sx={{ borderRadius: 3 }}>
                                <CardContent>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                                                {jobPosting.title}
                                            </Typography>
                                            <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                                                {jobPosting.location || 'No location specified'}
                                            </Typography>
                                            <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                                                {jobPosting.job_type || 'No type specified'}
                                            </Typography>
                                        </Box>
                                        <Chip
                                            label={jobPosting.is_active ? 'Active' : 'Inactive'}
                                            color={jobPosting.is_active ? 'success' : 'default'}
                                            size="small"
                                            icon={jobPosting.is_active ? <ActiveIcon /> : <InactiveIcon />}
                                        />
                                    </Box>
                                    
                                    <Typography variant="body2" sx={{ mb: 2 }}>
                                        {truncateText(jobPosting.description, 120)}
                                    </Typography>
                                    
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                        <Typography variant="caption" color="textSecondary">
                                            Applications: {jobPosting.careers_count || 0}
                                        </Typography>
                                        <Typography variant="caption" color="textSecondary">
                                            {formatDate(jobPosting.created_at)}
                                        </Typography>
                                    </Box>
                                    
                                    <Divider sx={{ my: 2 }} />
                                    
                                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => handleViewJobPosting(jobPosting)}
                                            sx={{ borderRadius: 3 }}
                                        >
                                            View
                                        </Button>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => handleEditJobPosting(jobPosting)}
                                            sx={{ borderRadius: 3 }}
                                        >
                                            Edit
                                        </Button>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => handleToggleStatus(jobPosting)}
                                            sx={{ borderRadius: 3 }}
                                        >
                                            {jobPosting.is_active ? 'Deactivate' : 'Activate'}
                                        </Button>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            color="error"
                                            onClick={() => handleDeleteJobPosting(jobPosting)}
                                            sx={{ borderRadius: 3 }}
                                        >
                                            Delete
                                        </Button>
                                    </Box>
                                </CardContent>
                            </Card>
                        ))
                    )}
                </Stack>
            )}

            {/* Pagination */}
            {pagination && pagination.last_page > 1 && (
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                    <Pagination
                        count={pagination.last_page}
                        page={currentPage}
                        onChange={handlePageChange}
                        color="primary"
                        showFirstButton={false}
                        showLastButton={false}
                        size={isMobile ? "small" : "medium"}
                        siblingCount={isMobile ? 0 : 1}
                    />
                </Box>
            )}

            {/* Results Summary */}
            {pagination && (
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                    <Typography variant="body2" color="textSecondary">
                        Showing {pagination.from} to {pagination.to} of {pagination.total} job postings
                    </Typography>
                </Box>
            )}

            {/* Job Posting Form Dialog */}
            <JobPostingForm
                open={openForm}
                jobPosting={selectedJobPosting}
                onClose={() => {
                    setOpenForm(false);
                    setSelectedJobPosting(null);
                }}
                onSuccess={() => {
                    setOpenForm(false);
                    setSelectedJobPosting(null);
                    loadJobPostings();
                }}
            />

            {/* Job Posting View Dialog */}
            <JobPostingView
                open={openView}
                jobPosting={selectedJobPosting}
                onClose={() => {
                    setOpenView(false);
                    setSelectedJobPosting(null);
                }}
            />

            {/* Delete Confirmation Dialog */}
            <ConfirmationDialog
                open={deleteDialog}
                title="Delete Job Posting"
                message={`Are you sure you want to delete "${jobPostingToDelete?.title}"? This action cannot be undone.`}
                onConfirm={confirmDelete}
                onCancel={() => {
                    setDeleteDialog(false);
                    setJobPostingToDelete(null);
                }}
            />
        </Box>
    );
};

export default JobPostings;
